import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRows: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  rowsPerPageOptions?: number[];
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalRows,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
}) => {
  const startRow = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRow = Math.min(currentPage * rowsPerPage, totalRows);

  // Generate page numbers to show (max 5 pages)
  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-white border-t border-slate-200 text-xs sm:text-sm">
      {/* Left Info & Rows Per Page */}
      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-4 text-slate-500 w-full sm:w-auto">
        <div className="flex items-center gap-1.5">
          <span className="text-xs">Baris:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              onRowsPerPageChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="border border-slate-300 rounded px-2 py-1 bg-slate-50 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-sky-500 cursor-pointer"
          >
            {rowsPerPageOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
        <span className="text-xs text-slate-500 font-medium">
          {totalRows === 0 ? '0 data' : `${startRow}–${endRow} dari ${totalRows.toLocaleString('id-ID')} data`}
        </span>
      </div>

      {/* Right Navigation Controls */}
      <div className="flex items-center justify-center gap-1 w-full sm:w-auto">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
          title="Halaman Pertama"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
          title="Halaman Sebelumnya"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Desktop & Tablet Page Number Buttons */}
        <div className="hidden sm:flex items-center gap-1 px-1">
          {getPageNumbers().map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`min-w-7 h-7 px-2 flex items-center justify-center rounded-md text-xs font-bold transition cursor-pointer ${
                pageNum === currentPage
                  ? 'bg-sky-600 text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200'
              }`}
            >
              {pageNum}
            </button>
          ))}
        </div>
        
        {/* Mobile Page Indicator */}
        <span className="sm:hidden px-2.5 py-1 bg-slate-100 border border-slate-200 rounded text-slate-700 font-bold text-xs">
          Hal {currentPage} / {totalPages}
        </span>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
          title="Halaman Selanjutnya"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
          title="Halaman Terakhir"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
